import React from 'react';
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import FileSaveIcon from '../../../../../assets/svg/file_save.svg?react';
import BrushIcon from '../../../../../assets/svg/brush.svg?react';
import styles from './DocumentListVault.module.scss';
import { v4 as uuidv4 } from 'uuid';
import { Document, DocumentListVaultProps } from './types';
import FileTypeDownloadModal from '../../../../common/file-type-download-modal/FileTypeDownloadModal';
import { useFileTypeDownload } from '../../../../common/file-type-download-modal/useFileTypeDownload';
import { ProgressOverlay } from '../../../../common/progress-overlay/ProgressOverlay';
import { DocumentsEditButton } from '../../../../common/document-edit-button/DocumentsEditButton';
import MessageModal from '../../../../common/message-modal/MessageModal';
import { useDeleteMessageModal } from '../../hooks/useDeleteMessageModal';
import { useLocation, useNavigate } from 'react-router-dom';
import ContractEditIcon from '../../../../../assets/svg/menu/contract-edit.svg?react';
import { useDocumentSigning } from '../../hooks/useDocumentSigning';
import { useDocumentsFunctions } from '../../hooks/useDocumentsFunctions';
import { DocumentsListRow } from './DocumentsListRow';
import { useAppSelector } from '../../../../common/hooks/useAppSelector';
import { useAssignProfilePersonModal } from '../../../../common/assign-profile-person-modal/useAssignProfilePersonModal';
import { AssignProfilePersonModal } from '../../../../common/assign-profile-person-modal/AssignProfilePersonModal';

import { useAppDispatch } from '../../../../common/hooks/useAppDispatch';
import { selectAllEmployees } from '../../../../slices/employees/employeesSlice';
import { selectAllSuppliers } from '../../../../slices/suppliers/suppliersSlice';
import { selectAllCustomers } from '../../../../slices/customers/customersSlice';
import { selectBusinessProfileContacts } from '../../../../slices/business-profile/businessProfileSlice';
import { showOverlay } from '../../../../slices/show-progress-overlay/showProgressOverlay';
import { useHasClerkRole } from '../../../../common/hooks/useHasClerkRole';
import InfoIcon from '@mui/icons-material/Info';
import { USER_PERMISSIONS } from '../../../../common/constants/permissions';
import { useCheckPermission } from '../../../../common/hooks/useCheckPermission';
import { useDocumensoRedirect } from '../../../../common/hooks/useDocumensoRedirect';
import { ROUTES } from '../../../../common/constants/routes';

const DocumentListVault = ({ documents }: DocumentListVaultProps) => {
  const { t } = useTranslation();
  const userIsEmployee = useHasClerkRole('org:employee');
  const checkPermission = useCheckPermission();
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;
  const { navigateToDocumentPageTab } = useDocumentsFunctions();
  const dispatch = useAppDispatch();
  const employees = useAppSelector(selectAllEmployees);
  const suppliers = useAppSelector(selectAllSuppliers);
  const customers = useAppSelector(selectAllCustomers);
  const businessContacts = useAppSelector(selectBusinessProfileContacts);
  const { redirectToDocumenso, isLoading: False } = useDocumensoRedirect();
  const checkProfilePersonsAvailability = (category: string) => {
    switch (category) {
      case 'employment':
        return employees?.length > 0;
      case 'business':
        return businessContacts?.length > 0;
      case 'customers':
        return customers?.length > 0;
      case 'suppliers':
        return suppliers?.length > 0;
    }
  };

  const disAllowedExtensions = ['jpg', 'jpeg', 'heic'];
  const {
    setModalOpen: setDeleteMessageModalOpen,
    modalOpen: deleteMessageModalOpen,
    pendingStatus: documentDeletePendingStatus,
    documentToDelete,
    setDocumentToDelete,
    handleDeleteDocument,
  } = useDeleteMessageModal();
  const {
    setModalOpen: setFileTypeModalOpen,
    modalOpen: fileTypeModalOpen,
    handleCloseModal: handleCloseFileTypeModal,
    setDocument: setDocumentForDownloading,
    document: documentForDownloading,
    setFileName: setFileNameForDownloading,
    fileName: fileNameForDownloading,
    fileType: fileTypeForDownloading,
    setFileType: setFileTypeForDownloading,
    handleDownloadContract,
    downloadDocumentUploadItem,
    handleDownloadHTML,
    pendingStatus: downloadPendingStatus,
  } = useFileTypeDownload();
  const {
    document: documentToSign,
    setDocument: setDocumentToSign,
    handleSignOriginalDocument,
    handleSignLatestVersion,
    setOpenAssignPersonDialog,
    openAssignPersonDialog,
  } = useDocumentSigning();
  const { patchDocumentWithProfilePerson, setAssignee, assignee } = useAssignProfilePersonModal(
    documentToSign?.category,
    documentToSign
  );

  const renderTableHeader = () => (
    <TableHead>
      <TableRow className={styles.header_row}>
        <TableCell className={styles.header_cell}>
          <Typography variant='body2' className={styles.header_text}>
            {t('documentsTable.fileName')}
          </Typography>
        </TableCell>
        <TableCell className={styles.header_cell}>
          <Typography variant='body2' className={styles.header_text}>
            {t('documentsTable.profile')}
          </Typography>
        </TableCell>
        <TableCell className={styles.header_cell}>
          <Typography variant='body2' className={styles.header_text}>
            {t('documentsTable.workflow')}
          </Typography>
        </TableCell>
        <TableCell className={styles.header_cell}>
          <Typography variant='body2' className={styles.header_text}>
            {t('documentsTable.date')}
          </Typography>
        </TableCell>
        <TableCell className={styles.header_cell}>
          <Typography variant='body2' className={styles.header_text}>
            {t('labels.endRenewalDate')}
          </Typography>
        </TableCell>
        <TableCell className={styles.header_cell}>
          <Typography variant='body2' className={styles.header_text}>
            {t('documentsTable.fileType')}
          </Typography>
        </TableCell>
        <TableCell className={styles.header_cell}>
          <Typography variant='body2' className={styles.header_text}>
            {t('headers.signed')}
          </Typography>
        </TableCell>
        <TableCell className={`${styles.header_cell} ${styles.actions_header}`}>
          <Typography variant='body2' className={styles.header_text}>
            {t('documentsTable.actions')}
          </Typography>
        </TableCell>
      </TableRow>
    </TableHead>
  );

  const renderActionButtons = (doc: Document) => {
    const checkDocumentEditingPermission = () => {
      switch (doc.category) {
        case 'employment':
          return checkPermission(USER_PERMISSIONS.ORG_EMPLOYEE_PROFILE_UPDATE_DOCUMENT);
        case 'business':
          return checkPermission(USER_PERMISSIONS.ORG_BUSINESS_PROFILE_UPDATE_DOCUMENT);
        case 'customers':
          return checkPermission(USER_PERMISSIONS.ORG_CUSTOMER_PROFILE_UPDATE_DOCUMENT);
        case 'suppliers':
          return checkPermission(USER_PERMISSIONS.ORG_SUPPLIER_PROFILE_UPDATE_DOCUMENT);
      }
    };

    const checkDocUploadOrContractEditingPermission = () => {
      if ('contract_generation' in doc) {
        return checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_CONTRACT_EDIT);
      } else {
        return checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_UPLOAD_EDIT);
      }
    };

    const checkDocUploadOrContractDeletingPermission = () => {
      if ('contract_generation' in doc) {
        return checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_CONTRACT_DELETE);
      } else {
        return checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_UPLOAD_DELETE);
      }
    };

    const checkDocumentCategoryDeletingPermission = () => {
      switch (doc.category) {
        case 'employment':
          return checkPermission(USER_PERMISSIONS.ORG_EMPLOYEE_PROFILE_DELETE_DOCUMENT);
        case 'business':
          return checkPermission(USER_PERMISSIONS.ORG_BUSINESS_PROFILE_DELETE_DOCUMENT);
        case 'customers':
          return checkPermission(USER_PERMISSIONS.ORG_CUSTOMER_PROFILE_DELETE_DOCUMENT);
        case 'suppliers':
          return checkPermission(USER_PERMISSIONS.ORG_SUPPLIER_PROFILE_DELETE_DOCUMENT);
      }
    };

    const checkDocUploadOrContractReadingPermission = () => {
      if ('contract_generation' in doc) {
        return checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_CONTRACT_READ);
      } else {
        return checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_UPLOAD_READ);
      }
    };
    return (
      <Box className={styles.actions_container}>
        {doc?.is_flattened && (
          <Tooltip title={t('messages.flattenedPDFMessage')} arrow placement='top'>
            <InfoIcon color='primary' fontSize='small' style={{ marginRight: '8px' }} />
          </Tooltip>
        )}
        {!disAllowedExtensions.some((ext) => doc.name.toLowerCase().endsWith(`.${ext}`)) &&
          !doc.is_flattened && (
            <Tooltip
              title={
                userIsEmployee ||
                !checkDocumentEditingPermission() ||
                !checkDocUploadOrContractEditingPermission()
                  ? t('messages.contactAdministrator')
                  : t('buttons.edit')
              }
              placement='top'
              arrow
            >
              <IconButton
                size='small'
                disabled={
                  userIsEmployee ||
                  !checkDocumentEditingPermission() ||
                  !checkDocUploadOrContractEditingPermission()
                }
                onClick={
                  userIsEmployee ||
                  !checkDocumentEditingPermission() ||
                  !checkDocUploadOrContractEditingPermission()
                    ? null
                    : () => navigateToDocumentPageTab(doc, 0, false, false, true)
                }
                className={styles.action_button}
              >
                <BrushIcon />
              </IconButton>
            </Tooltip>
          )}
        {!disAllowedExtensions.some((ext) => doc.name.toLowerCase().endsWith(`.${ext}`)) &&
          !doc.is_flattened && (
            <Tooltip
              title={
                userIsEmployee || !checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_SIGNING_CREATE)
                  ? t('messages.contactAdministrator')
                  : doc.signing_doc_id && doc.status === 'COMPLETED'
                    ? t('buttons.signedDocument')
                    : doc.signing_doc_id && doc.status === 'PENDING'
                      ? t('buttons.documentInSigning')
                      : t('buttons.sign')
              }
              placement='top'
              arrow
            >
              <IconButton
                size='small'
                disabled={
                  userIsEmployee || !checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_SIGNING_CREATE)
                }
                onClick={
                  userIsEmployee || !checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_SIGNING_CREATE)
                    ? null
                    : () => {
                        if (doc.signing_doc_id) {
                          redirectToDocumenso(
                            `${ROUTES.DOCUMENTS}/${doc.signing_doc_id}?view=true&backUrl=${path}&activeTab=${doc.category}`
                          );
                        } else {
                          if (
                            (doc.customers.length === 0 &&
                              doc.employees.length === 0 &&
                              doc.suppliers.length === 0 &&
                              checkProfilePersonsAvailability(doc.category)) ||
                            ('document_upload_html' in doc && doc.document_upload_html.length > 0)
                          ) {
                            setDocumentToSign(doc);
                            setOpenAssignPersonDialog(true);
                          } else {
                            dispatch(showOverlay());
                            handleSignOriginalDocument(doc);
                          }
                        }
                      }
                }
                className={styles.action_button}
              >
                <ContractEditIcon />
              </IconButton>
            </Tooltip>
          )}
        <Tooltip
          title={
            checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_EXPORT)
              ? t('buttons.download')
              : t('messages.contactAdministrator')
          }
          placement='top'
          arrow
        >
          <IconButton
            size='small'
            disabled={!checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_EXPORT)}
            onClick={() => {
              if ('contract_generation' in doc) {
                setDocumentForDownloading(doc);
                setFileNameForDownloading(doc.name);
                setFileTypeModalOpen(true);
              } else {
                if (doc.document_upload_html.length > 0) {
                  setDocumentForDownloading(doc);
                  setFileTypeModalOpen(true);
                } else {
                  downloadDocumentUploadItem(doc.id, doc.name);
                }
              }
            }}
            className={styles.action_button}
          >
            <FileSaveIcon />
          </IconButton>
        </Tooltip>
        <DocumentsEditButton
          document={doc}
          isImage={disAllowedExtensions.some((ext) => doc.name.toLowerCase().endsWith(`.${ext}`))}
          getDocumentSummaryFn={
            checkPermission(USER_PERMISSIONS.ORG_AI_SUMMARY_CREATE)
              ? () => navigateToDocumentPageTab(doc, 1, false, true)
              : null
          }
          createTemplateFn={
            !doc.contract_generation &&
            !userIsEmployee &&
            !doc.is_flattened &&
            checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_TEMPLATE_CREATE)
              ? () => navigateToDocumentPageTab(doc, 0, true)
              : null
          }
          duplicateDocumentFn={
            !('contract_generation' in doc) &&
            checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_CONTRACT_CREATE)
          }
          deleteDocumentFn={
            checkDocumentCategoryDeletingPermission() &&
            checkDocUploadOrContractDeletingPermission()
              ? () => {
                  setDocumentToDelete(doc);
                  setDeleteMessageModalOpen(true);
                }
              : null
          }
          documentVersionsFn={
            checkDocUploadOrContractReadingPermission()
              ? () => navigateToDocumentPageTab(doc, 1)
              : null
          }
          documentLabelsFn={
            checkDocUploadOrContractReadingPermission()
              ? () => navigateToDocumentPageTab(doc, 1)
              : null
          }
        />
      </Box>
    );
  };

  return (
    <>
      <TableContainer
        component={Paper}
        className={styles.table_container}
        sx={{
          backgroundColor: 'transparent',
          boxShadow: 'none',
          maxHeight: 'calc(100vh - 179px)',
          overflow: 'auto',
        }}
      >
        <Table stickyHeader>
          {renderTableHeader()}
          <TableBody>
            {documents.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <p className={styles.empty_message}>{t('messages.emptyDocumentsMessage')}</p>
                </TableCell>
              </TableRow>
            )}
            {documents.map((doc) => (
              <DocumentsListRow
                key={`${doc.id}-${uuidv4()}`}
                doc={doc}
                renderActionButtons={renderActionButtons}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {fileTypeModalOpen && documentForDownloading && (
        <FileTypeDownloadModal
          onClose={() => {
            handleCloseFileTypeModal();
          }}
          open={fileTypeModalOpen}
          fileName={fileNameForDownloading}
          fileType={fileTypeForDownloading}
          handleFileTypeChange={setFileTypeForDownloading}
          handleFileNameChange={setFileNameForDownloading}
          document={documentForDownloading}
          handleDownloadContract={() => {
            handleDownloadContract(documentForDownloading.id);
          }}
          handleDownloadDocument={() =>
            downloadDocumentUploadItem(documentForDownloading.id, documentForDownloading.name)
          }
          handleDownloadHTML={() => {
            handleDownloadHTML(
              Math.max(...documentForDownloading.document_upload_html),
              documentForDownloading.name.endsWith('.pdf') ||
                documentForDownloading.name.endsWith('.docx') ||
                documentForDownloading.name.endsWith('.doc')
                ? documentForDownloading.name
                    .replace('.pdf', '')
                    .replace('.docx', '')
                    .replace('.doc', '')
                : documentForDownloading.name,
              documentForDownloading.du_filetype[0]
            );
          }}
        />
      )}
      {deleteMessageModalOpen && (
        <MessageModal
          open={deleteMessageModalOpen}
          modalTitle={t('dialogTitles.deleteDocument')}
          modalMessage={t('messages.deleteDocumentDialog')}
          actionButtonText={t('buttons.goBack')}
          actionButtonFn={() => {
            setDocumentToDelete(null);
            setDeleteMessageModalOpen(false);
          }}
          goBackButtonText={t('buttons.delete')}
          document={documentToDelete}
          onClose={() => {
            handleDeleteDocument();
          }}
        />
      )}
      {/*signingDialogOpen && (
        <MessageModal
          open={signingDialogOpen}
          modalMessage={t('messages.signingVersionMessage')}
          actionButtonText={t('buttons.signLatestVersion')}
          actionButtonFn={() => handleSignLatestVersion(documentToSign)}
          actionButtonAdditionalText={t('buttons.signOriginalDocument')}
          actionButtonAdditionalFn={() => handleSignOriginalDocument(documentToSign)}
          goBackButtonText={t('buttons.goBack')}
          document={documentToSign}
          onClose={() => {
            setDocumentToSign(null);
            setSigningDialogOpen(false);
          }}
        />
      )*/}
      {openAssignPersonDialog && documentToSign && (
        <AssignProfilePersonModal
          open={openAssignPersonDialog}
          onClose={() => {
            setOpenAssignPersonDialog(false);
            setDocumentToSign(null);
            setAssignee(null);
          }}
          modalTitle={t('headers.startSigning')}
          category={documentToSign?.category}
          document={documentToSign}
          goBackButtonText={t('buttons.goBack')}
          actionButtonText={t('buttons.signLatestVersion')}
          actionButtonTextSecondary={t('buttons.signOriginalDocument')}
          modalMessage={t('messages.signingRecommendations')}
          assignee={assignee}
          setAssignee={setAssignee}
          secondaryAction={() => {
            dispatch(showOverlay());
            if (assignee) {
              if (
                documentToSign?.category === 'suppliers' ||
                documentToSign?.category === 'business'
              ) {
                handleSignOriginalDocument(documentToSign, Number(assignee));
              } else {
                patchDocumentWithProfilePerson()
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  //@ts-ignore
                  .then((res) => handleSignOriginalDocument(res))
                  .catch(() => handleSignOriginalDocument(documentToSign));
              }
            } else {
              handleSignOriginalDocument(documentToSign);
            }
          }}
          primaryAction={() => {
            dispatch(showOverlay());
            if (assignee) {
              if (documentToSign?.category === 'suppliers') {
                handleSignLatestVersion(documentToSign, null, Number(assignee));
              } else {
                patchDocumentWithProfilePerson()
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  //@ts-ignore
                  .then((res) => handleSignLatestVersion(res))
                  .catch(() => handleSignLatestVersion(documentToSign));
              }
            } else {
              handleSignLatestVersion(documentToSign);
            }
          }}
        />
      )}
      {(downloadPendingStatus || documentDeletePendingStatus) && <ProgressOverlay />}
    </>
  );
};

export default DocumentListVault;
